<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-shop me-2"></i>Stores Management
      </h4>
      <div class="d-flex gap-3">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <button class="btn btn-primary" @click="storeModal = true; storeMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Store
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading stores...</p>
      </div>

      <!-- Stores Content -->
      <div v-if="!loading" class="stores-content fade-in">
        <!-- Stores Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ filteredStores.length }}</span>
            <span class="text-muted">Stores</span>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search store" class="mb-0 w-auto" />
        </div>

        <!-- Stores Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>CODE</th>
                <th>NAME</th>
                <th>ADDRESS</th>
                <th>PHONE</th>
                <th>TYPE</th>
                <th>STATUS</th>
                <th>CREATED</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(store, index) in paginatedStores" :key="store.id" class="store-row" :class="{'bg-light': index % 2 === 0}">
                <td>
                  <span class="fw-medium">{{ store.code }}</span>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <div class="store-avatar me-2" :class="`bg-${['primary', 'success', 'info', 'warning'][index % 4]}-light`">
                      <span class="avatar-text">{{ store.name.charAt(0).toUpperCase() }}</span>
                    </div>
                    <span class="fw-medium">{{ store.name }}</span>
                  </div>
                </td>
                <td>{{ store.address }}</td>
                <td>{{ store.phone }}</td>
                <td>{{ store.type }}</td>
                <td>
                    <span class="badge" :class="store.online ? 'bg-success' : 'bg-danger'">
                      <i class="bi" :class="store.online ? 'bi-check-circle' : 'bi-x-circle'"></i>
                      {{ store.online ? 'Online' : 'Offline' }}
                    </span>
                </td>
                <td>{{ formatDate(store.created_at) }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-primary" @click="viewStoreDetails(store)">
                    <i class="bi bi-eye"></i> View Details
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-center mt-4">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>


    <!-- Add/Edit Store Modal -->
    <Modal :open="storeModal" @closed="storeModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi" :class="storeMode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'"></i>
          {{ storeMode === 'add' ? 'Add New Store' : 'Edit Store' }}
        </h5>
        <button @click="storeModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="form-group col-md-6">
              <label for="code" class="form-label">
                <i class="bi bi-upc me-1"></i>Store Code
              </label>
              <input
                  type="text"
                  id="code"
                  class="form-control"
                  placeholder="Code"
                  v-model="store.code"
                  readonly
              >
              <small class="text-muted">Auto-generated code</small>
            </div>
            <div class="form-group col-md-6">
              <label for="name" class="form-label">
                <i class="bi bi-shop me-1"></i>Branch Name
              </label>
              <input
                  type="text"
                  id="name"
                  class="form-control"
                  placeholder="Enter branch name"
                  v-model="store.name"
              >
            </div>
            <div class="form-group col-md-6">
              <label for="storeAddress" class="form-label">
                <i class="bi bi-geo-alt me-1"></i>Address
              </label>
              <input
                  type="text"
                  id="storeAddressInput"
                  class="form-control"
                  placeholder="Enter address"
                  v-model="store.address"
              />
            </div>
            <div class="form-group col-md-6">
              <label for="phone" class="form-label">
                <i class="bi bi-telephone me-1"></i>Phone
              </label>
              <input
                  type="text"
                  id="phone"
                  class="form-control"
                  placeholder="Enter phone number"
                  v-model="store.phone"
              >
            </div>
          </div>
        </form>
        <div class="row mt-3">
          <div class="col-md-12">
            <PlacesAutocomplete
                ref="placesAutocomplete"
                input-id="storeAddressInput"
                :initial-position="store.latitude && store.longitude ? { lat: store.latitude, lng: store.longitude } : null"
                @place-updated="handlePlaceUpdated"
            />
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="storeModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button
            type="button"
            class="btn btn-primary"
            v-if="storeMode === 'add'"
            @click="addStore"
        >
          <i class="bi bi-plus-circle me-1"></i>Create Store
        </button>
        <button
            type="button"
            class="btn btn-primary"
            v-else
            @click="updateStore(store)"
        >
          <i class="bi bi-check-circle me-1"></i>Update Store
        </button>
      </div>
    </Modal>
    <!-- Add/Edit Store Modal End -->
  </Card>
</template>

<script>
import storeService from "@/api/storeService";
import formatDate from "../../utils/formatDate";
import {encrypt, decrypt} from "@/utils/crypto";
import Pagination from "@/components/pagination/Pagination.vue";
import PlacesAutocomplete from "@/components/places-autocomplete/PlacesAutocomplete.vue";

export default {
  name: "Stores",

  components: {
    Pagination,
    PlacesAutocomplete
  },

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      profile: {},
      organization: {},
      currentStore: {},
      loading: true,
      stores: [],
      storeMode: 'add',
      storeModal: false,
      dataTableSearch: '',
      dataTablePerPage: 10,
      dataTablePage: 1,
      store: {
        organization_id: this.organization.id,
        code: '',
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        phone: '',
        online: 0,
        online_name: '',
        catchphrase: '',
        background_image: ''
      },
      errors: {}
    }
  },

  methods: {
    formatDate,

    handlePlaceUpdated(details) {
      this.store.address = details.address;
      this.store.latitude = details.latitude;
      this.store.longitude = details.longitude;
      // You can also store street and country if your 'store' object has fields for them
    },

    getStores: async function () {
      this.loading = true;
      try {
        this.stores = await storeService.getStores();
      } catch (error) {
        console.error("Error fetching stores:", error);
      } finally {
        this.loading = false;
      }
    },
    viewStoreDetails(store) {
      this.$router.push(`/store/${store.id}`);
    },
    getStoreNewCode: async function () {
      try {
        let nextNo = await storeService.getStoreNextNo();
        this.store.code = nextNo.next;
      } catch (error) {
        console.error("Error fetching store new code:", error);
      }
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 0:
          return 'danger';
        case 1:
          return 'success';
      }
    },
    async addStore() {
      try {
        let response = await storeService.createStore(this.store);
        if (response) {
          this.storeModal = false;
          await this.getStores();
          await this.resetStoreForm();
        }
      } catch (error) {
        console.error("Error adding store:", error);
      }
    },
    async updateStore(store) {
      try {
        let response = await storeService.updateStore(this.store, store.id);
        if (response) {
          if (this.currentStore.id === store.id) {
            sessionStorage.setItem('store', encrypt(JSON.stringify(store)));
          }
          this.storeModal = false;
          await this.getStores();
        }
      } catch (error) {
        console.error("Error updating store:", error);
      }
    },
    async resetStoreForm() {
      await this.getStoreNewCode();
      this.store = {
        organization_id: this.organization.id,
        code: this.store.code,
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        phone: '',
        online: 0,
        online_name: '',
        catchphrase: '',
        background_image: ''
      };
      this.errors = {};
    },

  },

  created: async function () {
    try {
      this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
      this.currentStore = JSON.parse(decrypt(sessionStorage.getItem('store')));
      await this.getStores();
      await this.getStoreNewCode();
    } catch (error) {
      console.error("Error in created lifecycle hook:", error);
      this.loading = false;
    }
  },

  computed: {
    filteredStores(){
      if (!this.dataTableSearch) return this.stores;
      const term = this.dataTableSearch.toLowerCase();
      return this.stores.filter(s =>
          (s.code && s.code.toLowerCase().includes(term)) ||
          (s.name && s.name.toLowerCase().includes(term)) ||
          (s.address && s.address.toLowerCase().includes(term)) ||
          (s.phone && s.phone.toLowerCase().includes(term))
      );
    },
    paginatedStores(){
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredStores.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      return Math.ceil(this.filteredStores.length / this.dataTablePerPage) || 1;
    }
  },

  watch: {
    storeModal: function (newVal) {
      if (newVal) {
        if (this.storeMode === 'add') {
          this.resetStoreForm();
        }
        // Explicitly initialize the PlacesAutocomplete component after the modal is open and the DOM is updated
        this.$nextTick(() => {
          if (this.$refs.placesAutocomplete) {
            this.$refs.placesAutocomplete.initialize();
          }
        });
      }
    },
    dataTablePerPage(){
      this.dataTablePage = 1;
    }
  }
}
</script>

<style scoped>
/* Stores page styles */
.stores-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.store-row {
  transition: all 0.2s ease;
}

.store-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Store avatar */
.store-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

.avatar-text {
  color: inherit;
}

/* Background colors for avatars */
.bg-primary-light {
  background-color: #eef2ff;
  color: #4361ee;
}

.bg-success-light {
  background-color: #d1fae5;
  color: #10b981;
}

.bg-info-light {
  background-color: #dbeafe;
  color: #3b82f6;
}

.bg-warning-light {
  background-color: #fef3c7;
  color: #f59e0b;
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table th,
  .table td {
    padding: 0.75rem 1rem;
  }

  .store-avatar {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
}
</style>
